import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CasButton, CasDisplayField, CasStackRow, CasTabsView } from 'src/compass/shared/components';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import { formatDate, DateFormat, getRelativeTime } from 'src/compass/shared/util/casMoment';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import ClaimInfoTab from './ClaimInfoTab';
import { getColumnColorByDaysLeft, getColumnColorByStatus } from './ClaimListing';
import DeadReportsDialog from './DeadReportsDialog';
import ClaimTrackingStatusActions from './ClaimTrackingStatusActions';
import { ClaimCheckoutActionButton } from './ClaimCheckoutActionButton';
import ClaimCallbackDialog from './ClaimCallbackDialog';
import ActivityLogTab from './ActivityLogTab';

export type ClaimDetailsProps = {
  data: any;
  onUpdated: Function;
};

function sharepointInsuredName(insuredName: string|null) {
  insuredName = insuredName ?? "";
  const words = insuredName?.split(' ');
  const companyWords = [
    'llc',
    'inc.',
    'inc',
    'incorporated',
    'trust',
    'management',
    'lp',
    'llp',
    'ltd',
    'ltd.',
    'limited',
    'partnership',
    'partners',
    'co.',
    'co',
    'dba',
    'venture',
    'joint',
    'company',
    '&',
    'cattle',
    'farms',
    'beef',
    'gp',
    'feeders',
    'partners'  
  ];

  if (words?.find( s => companyWords.indexOf(s.toLowerCase()) >= 0) !== undefined) {
    // company
    return insuredName; 
  }    

  // individual
  if (words.length===2) {
    return `${words[1]}, ${words[0]}`;
  }
  
  if (words.length===3) {
    if (['iii', 'sr', 'jr'].indexOf(words[2].toLowerCase()) >= 0) {
      return `${words[1]} ${words[2]}, ${words[0]}`;
    }

    return `${words[2]}, ${words[0]} ${words[1]}`;
  }
  
  return insuredName;
}

function getClaimValue(data: any) {
  if(data?.hasClaim === "No Claim") return "No Claim";
  if( data?.hasClaim === "CLAIM" && data?.claimNumber === null) return "Converted";
  return data?.claimNumber;
}

export default function ClaimDetails({ data, onUpdated }: ClaimDetailsProps) {
  const deadReportsDialog = useCasBoolean();
  const callbackUpdateDialog = useCasBoolean();
  
  const encodedInsuredName = useMemo(
    () => encodeURIComponent(sharepointInsuredName(data?.insuredName)),
    [data?.insuredName]);

  const [btnSubmitted, setBtnSubmitted] = useState(false);

  const tabList = [
    { label: 'Info', children: <ClaimInfoTab data={data} onUpdated={onUpdated} /> },
    { label: 'Activity Log', children: <ActivityLogTab endorsementId={data.id} /> },
  ];

  const { mutate, isPending } = useUpdateMutation({
    url: 'Sce/Claims/ToggleIsFwdContract',
    params: {
      endorsementId: data.id,
    },
    onSuccess: (response) => {
      onSuccessEnqueueSnackbar(response);
      onUpdated();
    },
    showLoader: false,
  });

  const onButtonUpdated = () => {
    setBtnSubmitted(true);
    onUpdated();
  }

  return (
    <>
      {data?.deletedAtAIPFlag && (
        <Typography sx={{ textAlign: 'center', backgroundColor: 'yellow', padding: "4px" }} variant='h6'>Endorsement deleted at AIP</Typography>
      )}
      <CasStackRow className='bordered-cells'>
        <CasDisplayField label="Claim#" value={getClaimValue(data)}/>
        <CasDisplayField label="Type" value={data?.type} />
        <CasDisplayField label="HC" value={data?.totalNumberInsured} />
        <CasDisplayField
            label="Target (cwt)"
            value={data?.targetCWT}
          />          
        <CasDisplayField
            label="Coverage Price"
            value={data?.coveragePrice}
          />          
        <CasDisplayField
          label="Effective"
          value={formatDate(data?.effectiveDate, DateFormat.MDY)}
        />
        <CasDisplayField label="End" value={formatDate(data?.endDate, DateFormat.MDY)} />
      </CasStackRow>

      <CasStackRow className='bordered-cells'>
        <CasDisplayField
          label="Status"
          value={<Box sx={getColumnColorByStatus(data)}>{data?.status}</Box>}
        />
        <CasDisplayField
          label="Days Left"
          value={<Box sx={getColumnColorByDaysLeft(data)}>{data?.daysLeft}</Box>}
        />
        <CasDisplayField
            label="Last Activity"
            value={<>
              {data?.lastActivity}
              &nbsp;
              {data?.lastActivity && 
              <Typography sx={{opacity:.5}} variant="caption">{getRelativeTime(data?.lastActivityDate)}</Typography>}
            </>}
          />          
        <CasDisplayField
          label="Is Fwd Contract?"
          value={
            <Box sx={{ backgroundColor: data?.isFwdContract ? 'yellow' : 'transparent' }}>
              {data?.isFwdContract ? "Yes" : "No"}
              &nbsp;
              {isPending ? (
                '(updating)'
              ) : (
                <CasLink onClick={() => mutate({ isFwdContract: data?.isFwdContract ? 0 : 1 })}>
                  toggle
                </CasLink>
              )}
            </Box>
          }
        />
        <CasDisplayField
            label="What"
            value={data?.whatSent}
          />   
        <CasDisplayField
            label="Crop"
            value={data?.crop}
          />          
        <CasDisplayField
          label="State"
          value={data?.state}
        />
        <CasDisplayField
          label="County"
          value={data?.county}
        />
      </CasStackRow>

      {data?.callback?.assignedToEmployeeId && (
        <CasStackRow className="bordered-cells">
          <CasDisplayField
            label="Callback Assigned To"
            value={data?.callback?.assignedToEmployee}
            onClick={callbackUpdateDialog.setTrue}
          />
          <CasDisplayField
            label="Callback Notes"
            value={data?.callback?.callbackNotes}
            onClick={callbackUpdateDialog.setTrue}
          />
        </CasStackRow>
      )}

      <div style={{marginBottom: 6}}>
        <CasStackRow alignItems="center" justifyContent="space-between">
          <ClaimCheckoutActionButton data={data} onUpdated={onUpdated} />
          <CasStackRow>
            {data?.growerId && (
              <CasButton
                variant="text"
                target="_blank"
                href={`${data?.eHarvestBaseUri}policy/Grower/Details?oid=${data.growerId}`}
                rel="noopener noreferrer"
              >
                eHarvest
              </CasButton>
            )}
            <CasButton
              variant="text"
              target="_blank"
              href={`https://dbadmincompassagsolutions.sharepoint.com/sites/CompassAgInsurance/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FCompassAgInsurance%2FShared%20Documents%2FCompass%20Ag%20Insurance%2FLRP%2F${encodedInsuredName}`}
              rel="noopener noreferrer"
            >
              Sharepoint
            </CasButton>
          </CasStackRow>
        </CasStackRow>
      </div>

      <CasStackRow gapSize="large" alignItems="center" backgroundColor="#ecffec">
        {btnSubmitted 
          ? <CasStackRow>
              <b>Action successful</b> 
              <CasButton
                variant="text"
                color="secondary"
                size="small"
                onClick={()=>setBtnSubmitted(false)}
              >
                Show next actions
              </CasButton>
            </CasStackRow>
          : <ClaimTrackingStatusActions data={data} onUpdated={onButtonUpdated} />
        }
        <CasDisplayField
          label="Deads Reported"
          value={`${data?.deadsReported}${data?.unspecified ? '+' : ''}`}
          horizontal
          onClick={deadReportsDialog.setTrue}
        />
      </CasStackRow>

      <CasTabsView tabList={tabList} />

      {deadReportsDialog.value && (
        <DeadReportsDialog
          data={data}
          onClose={() => {
            onUpdated();
            deadReportsDialog.setFalse();
          }}
        />
      )}

      {callbackUpdateDialog.value && (
        <ClaimCallbackDialog
          defaultValues={{
            id: data?.callback?.id,
            type: data?.callback?.type,
            callbackNotes: data?.callback?.callbackNotes,
            assignedToEmployeeId: data?.callback?.assignedToEmployeeId,
          }}
          title="Update Callback"
          submitButtonLabel="Update"
          mutationType="update"
          url="/Sce/Claims/CallbackUpdate"
          endorsementId={data?.id}
          onClose={callbackUpdateDialog.setFalse}
          onUpdated={onUpdated}
        />
      )}
    </>
  );
}
